import type { GroupPublic } from '@repo/types/groups';

import { useGetUserGroupGrades } from '@repo/api/grade/useGetUserGroupGrades';
import { getUserGroupGradesPrefetch } from '@repo/api/grade/useGetUserGroupGradesPrefetch';
import { useQueryClient } from '@tanstack/react-query';

import GradeCard from '@/components/features/grades/GradeCard';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { getAvatar } from '@/utils/getAvatar';

interface GradeGroupSectionProps {
  userId?: number;
  group: GroupPublic;
  isOpen: boolean;
}

export default function GradeGroupSection({ userId, group, isOpen }: GradeGroupSectionProps) {
  const queryClient = useQueryClient();
  const { data: grades = [], isPending } = useGetUserGroupGrades(userId, group.id, isOpen);

  const prefetch = () => {
    if (typeof userId !== 'number') return;
    getUserGroupGradesPrefetch({ userId, groupId: group.id, queryClient });
  };

  return (
    <AccordionItem value={String(group.id)} className={'bg-background border-border-primary rounded-xl border'}>
      <AccordionTrigger onMouseEnter={prefetch} onFocus={prefetch} className={'items-center p-3 hover:no-underline'}>
        <div className={'flex flex-1 items-center gap-2'}>
          <Avatar size={'sm'}>
            {group.avatarURL ? <AvatarImage src={getAvatar(group.avatarURL)} alt={group.name} /> : null}
            <AvatarFallback>{group.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <span className={'text-foreground text-base font-semibold'}>{group.name}</span>
          {!isPending && <span className={'text-muted-foreground ms-auto text-xs'}>{grades.length} ocen</span>}
        </div>
      </AccordionTrigger>
      <AccordionContent className={'grid h-auto grid-cols-1 gap-6 p-3 lg:grid-cols-2'}>
        {isPending ? (
          <>
            <Skeleton className={'h-12 w-full'} />
            <Skeleton className={'h-12 w-full'} />
          </>
        ) : grades.length ? (
          grades.map((grade) => <GradeCard key={grade.id} grade={grade} />)
        ) : (
          <p className={'text-muted-foreground py-2 text-sm'}>Brak ocen w tej grupie</p>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
